import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  InputAdornment, IconButton, Alert, Divider, Chip, CircularProgress,
} from '@mui/material';
import {
  Visibility, VisibilityOff, LockOutlined, PersonOutline,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';

export default function LoginPage() {
  const { login, loginError, setLoginError } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);

  // Core login handler — always async, always awaited
  const handleLogin = async (u = username, p = password) => {
    if (!u || !p) {
      setLoginError('Please enter both username and password.');
      return;
    }
    setLoading(true);
    try {
      const result = await login(u, p);   // Phase 2: real API call
      if (result.success) {
        navigate('/dashboard', { replace: true });
      }
      // If !result.success, loginError is already set inside AuthContext
    } catch (err) {
      setLoginError('Unable to connect to server. Please check the backend is running.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0A1628 0%, #1565C0 50%, #0D47A1 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decorative circles */}
      {[...Array(6)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.05)',
            width: 80 + i * 120,
            height: 80 + i * 120,
            top: '50%', left: '50%',
            transform: 'translate(-50%,-50%)',
            animation: `pulse ${4 + i}s ease-in-out infinite alternate`,
            '@keyframes pulse': {
              from: { opacity: 0.3, transform: 'translate(-50%,-50%) scale(1)' },
              to:   { opacity: 0.08, transform: 'translate(-50%,-50%) scale(1.05)' },
            },
          }}
        />
      ))}

      <Card
        sx={{
          width: '100%', maxWidth: 440,
          borderRadius: 3,
          boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
          position: 'relative', zIndex: 10,
        }}
      >
        <Box sx={{ height: 6, background: 'linear-gradient(90deg, #1565C0, #00BCD4, #26A69A)', borderRadius: '12px 12px 0 0' }} />

        <CardContent sx={{ p: 4 }}>
          {/* Brand */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box
              component="img"
              src={logo}
              alt="HRMS AI Dashboard logo"
              sx={{ width: 72, height: 72, borderRadius: '16px', mb: 2, boxShadow: '0 8px 24px rgba(21,101,192,0.4)', objectFit: 'cover' }}
            />
            <Typography variant="h5" fontWeight={700} color="primary.dark">HRMS AI Dashboard</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>AI-Powered HR Observability Platform</Typography>
          </Box>

          {loginError && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2, fontSize: '1rem', '& .MuiAlert-icon': { fontSize: '1.5rem' } }}
              onClose={() => setLoginError('')}
            >
              {loginError}
            </Alert>
          )}

          <TextField
            fullWidth label="Username" value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            disabled={loading}
            InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutline color="action" /></InputAdornment> }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth label="Password" type={showPw ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            disabled={loading}
            InputProps={{
              startAdornment: <InputAdornment position="start"><LockOutlined color="action" /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPw(!showPw)} edge="end" disabled={loading}>
                    {showPw ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Button
            fullWidth variant="contained" size="large"
            onClick={() => handleLogin()}
            disabled={loading}
            sx={{ py: 1.5, fontSize: '1rem' }}
          >
            {loading
              ? <><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />Signing in…</>
              : 'Sign In'
            }
          </Button>

          <Divider sx={{ my: 3 }}>
            <Typography variant="subtitle1" color="text.secondary">HexaR · www.hexar2u.com</Typography>
          </Divider>

          <Typography variant="subtitle1" color="text.disabled" display="block" textAlign="center" mt={0.5}>
              Enterprise workforce intelligence · Real-time analytics · AI executive narrative · Conversational HR intelligence
          </Typography>
       {/*<Typography variant="caption" color="text.disabled" display="block" textAlign="center" mt={2}>
             Backend: Java Spring Boot 4 · DB: PostgreSQL / ClickHouse 
          </Typography>*/}

        </CardContent>
      </Card>
    </Box>
  );
}
